/**
 * WizardPreviewCard — shows a simple summary of what the user has entered so far.
 * Displayed after Step 2 (once we have retirement dates) to create early engagement.
 */

import { useMemo } from 'react';
import type { AllStepFormData } from '../../types/wizard';

interface WizardPreviewCardProps {
  formData: Partial<AllStepFormData>;
  currentStep: number;
}

export function WizardPreviewCard({ formData, currentStep }: Readonly<WizardPreviewCardProps>) {
  const preview = useMemo(() => {
    // Need at least persons data (Step 2) to show anything useful
    const persons = formData.persons as Array<{ retirementDate?: string; dateOfBirth?: string; fullName?: string }> | undefined;
    if (!persons || persons.length === 0 || !persons[0]?.retirementDate) return null;

    const person = persons[0];
    const retirementYear = person.retirementDate ? new Date(person.retirementDate).getFullYear() : null;
    let retirementAge: number | null = null;
    if (person.dateOfBirth && person.retirementDate) {
      const dob = new Date(person.dateOfBirth);
      const ret = new Date(person.retirementDate);
      retirementAge = ret.getFullYear() - dob.getFullYear();
      if (ret.getMonth() < dob.getMonth() || (ret.getMonth() === dob.getMonth() && ret.getDate() < dob.getDate())) {
        retirementAge--;
      }
    }
    const yearsToRetirement = retirementYear ? retirementYear - new Date().getFullYear() : null;

    // Total holdings value (available after Step 3)
    let totalHoldings = 0;
    const holdings = formData.holdings as Record<string, Array<{ currentValue?: number }>> | undefined;
    if (holdings) {
      for (const personHoldings of Object.values(holdings)) {
        if (Array.isArray(personHoldings)) {
          for (const h of personHoldings) {
            totalHoldings += h.currentValue ?? 0;
          }
        }
      }
    }

    return {
      retirementAge,
      yearsToRetirement,
      totalHoldings,
      hasHoldings: totalHoldings > 0,
    };
  }, [formData]);

  // Only show after Step 2 and before Step 10
  if (!preview || currentStep <= 2 || currentStep >= 10) return null;

  const formatCurrency = (value: number): string => {
    if (value >= 1_000_000) return `£${(value / 1_000_000).toFixed(1)}m`;
    if (value >= 1_000) return `£${Math.round(value / 1_000)}k`;
    return `£${Math.round(value)}`;
  };

  return (
    <div className="wizard-preview-card">
      <span className="wizard-preview-card-label">Your plan so far</span>
      <div className="wizard-preview-card-stats">
        {preview.retirementAge && (
          <div className="wizard-preview-stat">
            <span className="wizard-preview-stat-value">{preview.retirementAge}</span>
            <span className="wizard-preview-stat-label">Retirement age</span>
          </div>
        )}
        {preview.yearsToRetirement !== null && preview.yearsToRetirement > 0 && (
          <div className="wizard-preview-stat">
            <span className="wizard-preview-stat-value">{preview.yearsToRetirement}</span>
            <span className="wizard-preview-stat-label">Years to go</span>
          </div>
        )}
        {preview.hasHoldings && (
          <div className="wizard-preview-stat">
            <span className="wizard-preview-stat-value">{formatCurrency(preview.totalHoldings)}</span>
            <span className="wizard-preview-stat-label">Total pot</span>
          </div>
        )}
      </div>
      <p className="wizard-preview-card-hint">
        Complete all steps to see your full year-by-year projection.
      </p>
    </div>
  );
}
